import React from "react";
import { Tooltip } from "@mui/material";
import { DataSubType } from "oschema";

import {
  Badge,
  formatDate,
  formatNumber,
  jsonPreview,
  prettyJson,
  relativeTime,
  toDate,
  toneFor,
} from "../lib/format";
import {
  Field,
  enumOptions,
  isBooleanField,
  isNumeric,
  isStructured,
  isTemporal,
  parsesAsNumber,
} from "../lib/schema-field";

const Empty = () => (
  <span className="text-od-muted select-none" title="No value">
    —
  </span>
);

const Truncated: React.FC<{
  text: string;
  title?: string;
  mono?: boolean;
  wrap?: boolean;
}> = ({ text, title, mono, wrap }) => {
  const cls = `block w-full ${
    wrap ? "whitespace-normal break-words" : "truncate"
  } ${mono ? "font-mono text-[12px]" : ""}`;
  // The grid ellipsizes and keeps the rest in a tooltip. The detail drawer
  // exists so that rest is actually readable, so wrap skips both.
  if (wrap) return <span className={cls}>{text}</span>;
  return (
    <Tooltip title={title ?? text} placement="top-start" enterDelay={500}>
      <span className={cls}>{text}</span>
    </Tooltip>
  );
};

/** Columns whose short string values read as a state rather than as prose. */
const STATUS_NAME = /(^|_)(status|state|stage|priority|severity|type|level|kind|tier)($|_)/i;

const isBadgeWorthy = (value: unknown, field?: Field): boolean => {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (trimmed === "" || trimmed.length > 28) return false;
  if (enumOptions(field)) return true;
  return !!field && STATUS_NAME.test(field.name);
};

type Props = {
  value: unknown;
  field?: Field;
  /**
   * Detail drawer. The grid keeps a one-line ellipsis so a column stays one
   * row tall; the drawer is where that value is supposed to be readable.
   */
  wrap?: boolean;
};

/**
 * Renders one value the way its type deserves. Every read-only surface in the
 * app funnels through here so a timestamp, a JSON blob or a status never gets
 * two different treatments on two different screens.
 */
export const DataCell: React.FC<Props> = ({ value, field, wrap }) => {
  if (value === null || value === undefined || value === "") return <Empty />;

  if (isBooleanField(field) || typeof value === "boolean") {
    const on = !!value && value !== "false" && value !== "0";
    return (
      <span className="inline-flex items-center gap-1.5">
        <span
          aria-hidden
          className="h-1.5 w-1.5 rounded-full"
          style={{
            backgroundColor: on ? "var(--od-success)" : "var(--od-muted)",
          }}
        />
        <span className={on ? "text-od-text" : "text-od-muted"}>
          {on ? "Yes" : "No"}
        </span>
      </span>
    );
  }

  const temporal =
    (isTemporal(field) && !parsesAsNumber(value)) || (!field && isIsoish(value));
  if (temporal) {
    const date = toDate(value);
    if (!date) return <Truncated text={String(value)} wrap={wrap} />;
    const dateText = formatDate(value, field);
    if (wrap) {
      return <span className="block w-full whitespace-normal tabular-nums">{dateText}</span>;
    }
    return (
      <Tooltip
        title={`${date.toLocaleString()} · ${relativeTime(date)}`}
        placement="top-start"
      >
        <span className="block w-full truncate tabular-nums">{dateText}</span>
      </Tooltip>
    );
  }

  if (isStructured(field) || (typeof value === "object" && value !== null)) {
    if (wrap) {
      return (
        <pre className="m-0 max-h-80 overflow-auto whitespace-pre-wrap break-words font-mono text-xs leading-4 text-od-text">
          {prettyJson(value)}
        </pre>
      );
    }
    return (
      <Tooltip
        title={
          <pre className="m-0 max-h-80 max-w-md overflow-auto whitespace-pre-wrap break-words font-mono text-xs leading-4">
            {prettyJson(value)}
          </pre>
        }
        placement="top-start"
      >
        <span className="block w-full truncate font-mono text-[12px] text-od-muted">
          {jsonPreview(value)}
        </span>
      </Tooltip>
    );
  }

  if ((isNumeric(field) && parsesAsNumber(value)) || typeof value === "number") {
    return (
      <span className="od-num block w-full whitespace-nowrap tabular-nums">
        {formatNumber(value, field)}
      </span>
    );
  }

  if (field?.subType === DataSubType.COLOR && /^#?[0-9a-f]{3,8}$/i.test(String(value))) {
    const hex = String(value).startsWith("#") ? String(value) : `#${value}`;
    return (
      <span className="inline-flex items-center gap-2">
        <span
          aria-hidden
          className="h-3.5 w-3.5 shrink-0 rounded border border-od-border"
          style={{ backgroundColor: hex }}
        />
        <span className={`${wrap ? "break-all" : "truncate"} font-mono text-[12px]`}>{hex}</span>
      </span>
    );
  }

  if (isBadgeWorthy(value, field)) {
    const text = String(value);
    return <Badge value={text} tone={toneFor(text)} />;
  }

  // Emails and URLs used to render as <a> with stopPropagation so a click
  // opened mailto:/a new tab instead of the row's edit pane. Keep them plain
  // text so the grid click (and Enter) behave like any other cell.
  const text = String(value);
  const mono =
    field?.subType === DataSubType.UUID ||
    field?.subType === DataSubType.IP_ADDRESS ||
    field?.subType === DataSubType.MAC_ADDRESS;

  return <Truncated text={text} mono={mono} wrap={wrap} />;
};

const isIsoish = (value: unknown) =>
  typeof value === "string" && /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2})/.test(value);
